import React, { Fragment, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { ClassicEditor } from "ckeditor5";
import { defaultEditorConfig } from "./defaultEditorConfig.ts";
import * as styles from "./CKEditor5.styles.ts";
import "ckeditor5/ckeditor5.css";
import "./CKEditor5.css";
import type { EventInfo } from "ckeditor5";
import type { CKEditorProps } from "./CKEditor5.types.ts";
import { Skeleton } from "src/components/Skeleton/Skeleton.tsx";
import { Placeholders } from "src/components/InputPlaceholders/InputPlaceholders.tsx";
import { Flex } from "src/components/Flex.tsx";
import { useCKEditorInsertion } from "src/components/CKEditor5/useCKeditorInsertion.ts";
import type { TextTemplatePlaceholder } from "@exonn/js-sdk";
import { InputLabel } from "src/components/InputLabel/InputLabel.tsx";
import { InputErrorMessage } from "src/components/InputErrorMessage/InputErrorMessage.tsx";

const placements = [
  "topLeft",
  "topRight",
  "bottomLeft",
  "bottomRight",
] as const;

export function CKEditor5(props: CKEditorProps) {
  const {
    className,
    config: newConfig,
    replaceConfig = false,
    height,
    label,
    helperText,
    required,
    minHeight,
    placeholders,
    onSelectPlaceholder,
    maxHeight,
    onSetEditorRef,
    defaultValue,
    value,
    error,
    floatingError = false,
    additionalElems,
    disabled = false,
    isLoading = false,
    onFocus,
    onChange: onControlledChange,
    additionalLabelElems,
    ...otherProps
  } = props;

  const containerRef = useRef<HTMLDivElement | null>(null);
  // const editorRef = useRef<CKEditor<ClassicEditor> | null>(null);
  const [editorRef, setEditorRef] = useState<ClassicEditor | null>();
  const [editableRef, setEditableRef] = useState<HTMLElement | null>();

  const initialConfig = replaceConfig
    ? newConfig
    : { ...defaultEditorConfig, ...(newConfig || {}) };

  const onChange = (_: EventInfo, editor: ClassicEditor) => {
    const data = editor.getData();

    if (disabled) {
      return;
    }

    if (editorRef && value !== editorRef.getData()) {
      onControlledChange?.(data);
    }
  };

  const onReady = (editor: ClassicEditor) => {
    setEditorRef(editor);
    onSetEditorRef?.(editor);
    setEditableRef(editor.ui.view.editable.element?.parentElement);
  };

  useEffect(() => {
    if (editorRef && value !== editorRef.getData()) {
      editorRef.setData(value ?? "");
    }
  }, [value, editorRef]);

  const { saveSelection, restoreSelection, insertText } =
    useCKEditorInsertion();

  const handlePlaceholderSelect = (placeholder: TextTemplatePlaceholder) => {
    onSelectPlaceholder?.(placeholder);
    const editor = editorRef;
    if (!editor) {
      return;
    }
    const code =
      placeholder.type === "conditional"
        ? `{% if ${placeholder.value} %} ... {% endif %}`
        : `{{ ${placeholder.value} }}`;
    const updated = insertText(editor, code);
    // Notify RHF
    onControlledChange?.(updated);

    // Restore caret inside CKEditor
    restoreSelection(editor);
  };

  // todo: wrap in error boundary

  return (
    <Flex flexDirection="column" gap={"15px"}>
      <Placeholders
        onSelectPlaceholder={handlePlaceholderSelect}
        placeholders={placeholders}
      />
      <Skeleton isLoading={isLoading}>
        <div>
          {label && (
            <InputLabel
              label={label}
              required={required}
              helperText={helperText}
              additionalElems={additionalLabelElems}
            />
          )}
          <div
            ref={containerRef}
            className={className}
            css={styles.editor({
              height,
              error,
              minHeight,
              maxHeight,
              disabled,
            })}
          >
            <CKEditor
              data={defaultValue} // todo
              editor={ClassicEditor}
              config={initialConfig}
              disabled={disabled}
              onReady={onReady}
              onChange={onChange}
              onFocus={(event, editor) => {
                saveSelection(editor);
                onFocus?.(event, editor);
              }}
              onBlur={(event, editor) => {
                saveSelection(editor);
              }}
              {...otherProps}
            />

            {editableRef &&
              additionalElems &&
              placements.map((placement, index) => {
                const element = additionalElems[placement];
                return (
                  <Fragment key={index}>
                    {element &&
                      createPortal(
                        <div css={styles.additionalElem(placement)}>
                          {element}
                        </div>,
                        editableRef,
                      )}
                  </Fragment>
                );
              })}
          </div>
          {error && (
            <InputErrorMessage floating={floatingError} message={error} />
          )}
        </div>
      </Skeleton>
    </Flex>
  );
}
